import Modal from "@/common/components/Modal";
import SmallBadge from "@/common/components/SmallBadge";
import useModalMessagesSimple from "@/common/hooks/useModalMessagesSimple";
import useUserPermissions from "@/common/hooks/useUserPermissions";
import { cn } from "@/common/utils/cn";
import { useQueryClient } from "@tanstack/react-query";
import { BellPlusIcon } from "lucide-react";
import { useId, useState } from "react";
import { Tooltip } from "react-tooltip";
import { useMaintenanceEstablishmentChoiceAuctionEntryFollow } from "../../../hooks/useMaintenanceEstablishmentChoiceAuctionEntryFollow";

interface FollowAuctionProps {
  auctionEntryId: string | undefined;
}

const FollowAuction = ({
  auctionEntryId,
}: FollowAuctionProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const tooltipId = useId();

  const queryClient = useQueryClient();

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions();

  const hasFollowPermission = !isUserPermissionsPending
    ? hasUserPermission('Establishment::Maintenance::EstablishmentChoice::AuctionEntries::Follow')
    : true;

  const {
    setMessages
  } = useModalMessagesSimple({
    isError: true,
  });

  const {
    mutate: follow,
    isPending: followIsPending,
  } = useMaintenanceEstablishmentChoiceAuctionEntryFollow({
    auctionEntryId,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['maintenanceEstablishmentChoiceAuctionEntryUpdatedSummary', auctionEntryId],
        });

        setIsOpen(false);
      },
      onError: (error) => {
        setMessages(error, { errorResponse: true });
      }
    }
  });

  return (
    <>
      <SmallBadge
        data-tooltip-id={!hasFollowPermission && !isUserPermissionsPending ? tooltipId : undefined}
        LeftIcon={BellPlusIcon}
        text="Acompanhar"
        className={cn(
          "transition-colors",
          hasFollowPermission
            ? "bg-success-secondary hover:bg-success-hover cursor-pointer"
            : "bg-success-secondary opacity-50 cursor-default"
        )}
        onClick={hasFollowPermission ? () => setIsOpen(true) : undefined}
      />

      {!hasFollowPermission && !isUserPermissionsPending && (
        <Tooltip id={tooltipId}>Você não tem permissão</Tooltip>
      )}

      <Modal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        bodyClassName='max-h-[calc(90vh-72px-48px)] overflow-auto'
        title="Acompanhar leilão?"
        titleClass="text-2xl"
        subtitleClassName="text-base mt-1"
        useCancellableFooter
        className="rounded-lg overflow-hidden"
        cancelButtonClassName="bg-white-100 rounded-es-lg"
        confirmButtonClassName="bg-primary-blue rounded-ee-lg"
        confirmButtonIsLoading={followIsPending}
        disabled={followIsPending || !auctionEntryId}
        showClose={false}
        onConfirm={() => follow()}
        preventAutoFocus
      />
    </>
  );
}

export default FollowAuction;
